import React, { FC, useEffect, useRef, useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import L, { divIcon, MarkerCluster } from 'leaflet';
import { Map as LeafletMap, Polyline, ZoomControl as LeafletZoomControl } from 'react-leaflet';
import { ITrackPoint } from '../../../models/types';
import { getOverlayCornersFromGbox } from '../../../utils/planCorners.utils';
import WMSTileLayer from '../../Main/Map/WMSTileLayer';
import { Dispatch, iRootState } from '../../../store';
import { connect } from 'react-redux';
import ReactDistortableImageOverlay from 'react-leaflet-distortable-imageoverlay';
import StartAndFinishMarker from './StartAndFinishMarker';
import Grid from '@material-ui/core/Grid';
import HoverMarkerPoint from './HoverMarkerPoint';
import { renderToString } from 'react-dom/server';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { getColor } from '../../../theme';
import AreaListItem from '../../Main/Map/AreaListItem';
import BeaconMarkerListItem from '../../Main/Map/BeaconMarkerListItem';
import TimelineBrush from './TimelineBrush';
import moment from 'moment';

import '../../Main/Map/components/Ruler/index'; // Импортируем leaflet-ruler
import '../../Main/Map/components/Ruler/index.css'; // Импортируем стили leaflet-ruler
import { Dropdown } from 'antd';
// tslint:disable-next-line:prettier
import type { MenuProps } from 'antd';

const useStyles = makeStyles(({ getShadow }) => ({
  container: { position: 'relative', height: '100%' },
  root: {
    zIndex: 0,
    height: '100%',
    marginLeft: 380,
    paddingBottom: 168,
    backgroundColor: getColor('constant-greyscale-0'),
    '& .leaflet-bottom': {
      bottom: 128
    },
    // Маркеры редактирования зон
    '& .leaflet-editing-icon': {
      backgroundColor: getColor('constant-apple-dark'),
      borderColor: getColor('constant-apple-dark'),
      width: '16px  !important',
      height: '16px  !important'
    },
    '& .leaflet-edit-move': {
      backgroundColor: getColor('constant-greyscale-0'),
      borderColor: getColor('constant-greyscale-400')
    },
    '& .leaflet-image-layer': {
      boxShadow: getShadow('low')
    }
  },
  point: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: getColor('constant-blackberry-light'),
    width: 28,
    height: 28,
    fontWeight: 500,
    color: getColor('text-inverted')
  },
  count: {
    borderRadius: '50%',
    width: 16,
    height: 16
  },
  download: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 1000
  }
}));

const mapState = (state: iRootState) => ({
  control: state.infrastructureMap.control,
  plan: state.reportTracks.plan,
  planImg: state.reportTracks.planImg,
  isAreas: state.reportTracks.layers.isAreas,
  isInfrastructure: state.reportTracks.layers.isInfrastructure
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    setControl: d.infrastructureMap.setControl,
    getPlan: d.reportTracks.getPlan,
    resetPlan: d.reportTracks.resetPlan,
    getPlanImg: d.reportTracks.getPlanImg,
    resetPlanImg: d.reportTracks.resetPlanImg,
    exportList: d.reportTracks.exportList
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = {
  track: ITrackPoint;
} & connectedProps;

const Map: FC<IProps> = ({
  track,
  control,
  setControl,
  getPlan,
  getPlanImg,
  plan,
  planImg,
  resetPlan,
  resetPlanImg,
  isAreas,
  isInfrastructure,
  exportList
}) => {
  const classes = useStyles();
  const { zoom, center } = control;
  const mapEl = useRef<LeafletMap | null>(null);
  const { userId, points, user } = track;
  const employeeName = [user?.lastname, user?.name, user?.secondname].filter(Boolean).join(' ');
  const { gbox, areas, beacons } = plan;
  const LatLng = L.latLng as any;
  const planOverlayCorners = getOverlayCornersFromGbox(gbox);

  const [range, setRange] = useState<[number, number] | null>(null);

  // Сбрасываем выделение диапазона при переключении треков,
  // чтобы не оставлять невалидные границы для нового набора точек
  useEffect(() => {
    setRange(null);
  }, [userId, track.from, track.to, points.length]);

  const drawPoints = useMemo(() => {
    if (!range) return points;
    const [from, to] = range;
    return points.filter((p: any) => {
      if (!p.time) return false;
      const t = moment(p.time).valueOf();
      return t >= from && t <= to;
    });
  }, [points, range]);

  // Инициализируем leaflet-ruler после рендеринга компонента
  useEffect(() => {
    if (mapEl.current) {
      (L.control as any).ruler().addTo(mapEl.current.leafletElement);
    }
  }, []);

  const handlerGetPlan = () => {
    if (points[0] && points[0].mapId) {
      resetPlan();
      const mapId = points[0].mapId;
      getPlan(mapId);
    } else {
      resetPlan();
    }
  };
  const dataStory = drawPoints.map((item: any) => {
    return item;
  });

  const fitToBounds = (pts = dataStory) => {
    if (mapEl.current && pts && pts.length) {
      const useMap = mapEl.current.leafletElement;
      useMap.fitBounds(pts, {
        padding: [150, 150],
        maxZoom: 20
      });

      const centerLL = useMap.getCenter();
      setControl({
        ...control,
        zoom: useMap.getZoom(),
        center: [centerLL.lat, centerLL.lng] as [number, number]
      });
    }
  };
  useEffect(() => {
    fitToBounds(points); // Устанавливаем zoom, чтобы был виден весь трек независимо от предыдущего диапазона
    handlerGetPlan(); // Загружаем план если есть
  }, [track]);

  useEffect(() => {
    if (range) {
      fitToBounds();
    }
  }, [range]);

  useEffect(() => {
    resetPlanImg();
    if (plan.id) {
      getPlanImg(plan.id);
    }
  }, [plan.id]);

  const renderIcon = (count: number) => (
    <Grid container justify="center" alignItems="center" className={classes.point}>
      <Grid item container justify="center" alignItems="center" className={classes.count}>
        {count}
      </Grid>
    </Grid>
  );
  const iconGroup = (cluster: MarkerCluster) => {
    const count = cluster.getChildCount();

    return divIcon({
      iconSize: undefined,
      html: renderToString(renderIcon(count))
    });
  };

  const handleMenuClick: MenuProps['onClick'] = e => {
    exportList({ reportFormat: e.key, mapId: plan.id });
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    exportList({ reportFormat: 'CSV', mapId: plan.id });
  };

  const items: MenuProps['items'] = [
    {
      label: 'Скачать трек в CSV',
      key: 'CSV'
      // icon: <UserOutlined />,
    },
    {
      label: 'Скачать трек в JSON',
      key: 'JSON'
      // icon: <UserOutlined />,
    }
  ];

  const menuProps = {
    items,
    onClick: handleMenuClick
  };

  const handleResetRange = () => {
    setRange(null);
    fitToBounds(points);
  };

  return (
    <div className={classes.container}>
      <LeafletMap
        ref={mapEl}
        center={center}
        zoom={zoom}
        className={classes.root}
        maxZoom={30}
        minZoom={3}
        animate={true}
        duration={1}
        zoomSnap={0}
        zoomControl={false}
        attributionControl={false}
        maxBounds={[
          [-86.5, -180],
          [85, 180]
        ]}
      >
        <WMSTileLayer />
        <LeafletZoomControl
          position="bottomright"
          zoomInTitle="Приблизить"
          zoomOutTitle="Отдалить"
        />
        <Dropdown.Button
          placement="bottom"
          type={'default'}
          size={'large'}
          className={classes.download}
          onClick={handleButtonClick}
          menu={menuProps}
        >
          Скачать
        </Dropdown.Button>
        {isAreas && areas && areas.map(area => <AreaListItem key={area.id} area={area} />)}
        {isInfrastructure &&
          beacons &&
          beacons.map(beacon => <BeaconMarkerListItem key={beacon.macAddress} item={beacon} />)}
        {dataStory.length >= 2 && (
          <Polyline weight={4} color={getColor('constant-blackberry-light')} positions={dataStory} />
        )}
        {dataStory.length > 2 && (
          <MarkerClusterGroup iconCreateFunction={iconGroup}>
            {dataStory.slice(1, -1).map((point, i) => (
              <HoverMarkerPoint point={point} key={i} />
            ))}
          </MarkerClusterGroup>
        )}
        {/*{point && <HoverMarkerPoint point={point} />}*/}
        {dataStory.length >= 1 && (
          <MarkerClusterGroup iconCreateFunction={iconGroup}>
            <StartAndFinishMarker points={dataStory} />
          </MarkerClusterGroup>
        )}
        {plan.id && planOverlayCorners && (
          <ReactDistortableImageOverlay
            key={plan.contentId}
            url={planImg}
            corners={planOverlayCorners.map(corner => new LatLng(corner.lat, corner.lng))}
          />
        )}
      </LeafletMap>
      <TimelineBrush
        points={points}
        value={range}
        onChange={setRange}
        onReset={handleResetRange}
        employeeName={employeeName}
      />
    </div>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Map);
